import { useStripe } from '@stripe/react-stripe-js';
import { BadRequest } from '@systemeio/util';
import { updateUserPlan } from 'modules/profile/user-plan/api/user-plan-api';
import { BillingPeriodEnum } from 'modules/profile/user-plan/enums/billing-period-enum';
import { useUserPlan } from 'modules/profile/user-plan/hooks/use-user-plan';
import { UpgradeUserPlanInterface, UserPlanInterface, UseUserPlanInterface } from 'modules/profile/user-plan/types/user-plan-interface';
import { getStripeCustomerConfirmIntentMethod } from 'modules/profile/user-plan/utils/user-plan-stripe-utils';
import React, { useState } from 'react';
import { Button, Modal, toast } from 'shared/components';
import { FieldErrorAndDescription } from 'shared/components/form/field-error-and-description';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
export interface UpgradePlanConfirmationModalProps {
  pricePlan: UserPlanInterface;
  billingPeriod: BillingPeriodEnum;
  paymentPageUrl: string;
  cost: string;
  active?: boolean;
  paymentMethod?: UseUserPlanInterface['customer']['paymentMethod'];
  isPlanUpgradeAvailable?: boolean;
}
const UpgradePlanConfirmationModal = ({
  pricePlan,
  billingPeriod,
  paymentPageUrl,
  cost,
  paymentMethod,
  isPlanUpgradeAvailable
}: UpgradePlanConfirmationModalProps) => {
  const {
    t
  } = useLocoTranslation();
  const [opened, setOpened] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState('');
  const {
    mutate
  } = useUserPlan();
  const stripeObject = useStripe();
  const onClose = () => {
    setOpened(false);
    setError('');
  };
  const onConfirm = async () => {
    const data: UpgradeUserPlanInterface = {
      userPlan: pricePlan.id,
      period: billingPeriod
    };
    try {
      const secureHandler = getStripeCustomerConfirmIntentMethod(stripeObject, paymentMethod);
      setIsFetching(true);
      const response = await updateUserPlan(data);
      if (response.data.redirect) {
        window.location.assign(response.data.redirect);
        return;
      }
      if (response.data.intentClientSecret && secureHandler) {
        const result = await secureHandler(response.data.intentClientSecret);
        if (result.error) {
          setError('Payment intent confirmation failed: ' + result.error.message);
          return;
        }
      }
      await mutate();
      toast.success(t('dashboard.settings.user_plan.upgrade_success_title'));
      onClose();
    } catch (e) {
      if (e instanceof BadRequest) {
        if (e.errors.common) {
          setError(e.errors.common.join(''));
        }
      } else {
        setError(t('core.error.internal_error_message'));
      }
    } finally {
      setIsFetching(false);
    }
  };
  const onModalClose = () => setError('');
  return <>
      <Button onClick={() => setOpened(true)} disabled={!isPlanUpgradeAvailable} data-test-id={`button-plan-${pricePlan.title.toLowerCase()}-upgrade`} variant="primary" size="sm">
        <strong className="truncate">{t('dashboard.settings.user_plan.upgrade_plan_title')}</strong>
      </Button>
      <Modal opened={opened} onClose={onClose} title={t('dashboard.settings.user_plan.upgrade_confirmation_title')} afterLeave={onModalClose} isFetching={isFetching}>
        <form className="flex flex-col gap-6">
          <div className="flex flex-col items-center justify-center gap-1">
            <div>
              {t('dashboard.settings.user_plan.upgrade_price_title')}: {cost}
            </div>
            <div className="flex">
              {t('dashboard.settings.user_plan.upgrade_confirmation_text')}
            </div>
          </div>
          <div className="tablet:[&>*]:flex-none tablet:[&>*]:w-auto flex justify-end gap-3 [&>*]:flex-1">
            <Button onClick={(e_0: React.MouseEvent<HTMLButtonElement>) => {
            e_0.preventDefault();
            onClose();
          }} disabled={isFetching} variant="secondary" size="md">
              {t('confirmation_modal.cancel')}
            </Button>
            <Button onClick={async (e_1: React.MouseEvent<HTMLButtonElement>) => {
            e_1.preventDefault();
            await onConfirm();
          }} type="submit" isLoading={isFetching} data-test-id={'confirm-plan-upgrade'} variant="primary" size="md">
              {t('confirmation_modal.confirm')}
            </Button>
          </div>
          <div className="flex justify-center">
            <a href={paymentPageUrl} className="text-text-link" target={'_self'}>
              {t('dashboard.settings.user_plan.upgrade_change_payment_method_title')}
            </a>
          </div>
          <FieldErrorAndDescription error={error} errorClassName={'text-center mt-2'} />
        </form>
      </Modal>
    </>;
};
export default UpgradePlanConfirmationModal;